'use client';
import React, { useState, FormEvent } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { X, CreditCard, Lock, CheckCircle, Loader2 } from 'lucide-react';
import { getStripe } from '@/lib/stripe/client';
import { createClient } from '@/lib/supabase/client';

interface StripePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (paymentType: 'deposit' | 'balance') => void;
  paymentType: 'deposit' | 'balance';
  amount: number;
  clientSecret: string;
  paymentScheduleId: string;
  eventDescription: string;
  clientEmail?: string;
  clientName?: string;
}

interface PaymentFormProps {
  onSuccess: () => void;
  onClose: () => void;
  paymentScheduleId: string;
  amount: number;
  paymentType: 'deposit' | 'balance';
  eventDescription: string;
  clientEmail?: string;
  clientName?: string;
}

function PaymentForm({ onSuccess, onClose, paymentScheduleId, amount, paymentType, eventDescription, clientEmail, clientName }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/#booking`,
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message ?? 'Payment failed. Please try again.');
      setIsProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      try {
        const supabase = createClient();
        const { error: confirmError } = await supabase.functions.invoke('confirm-payment', {
          body: {
            paymentIntentId: paymentIntent.id,
            sendReceipt: true,
            clientEmail: clientEmail || '',
            clientName: clientName || '',
          },
        });
        if (confirmError) {
          console.error('Backend confirm failed:', confirmError);
        }
      } catch (err) {
        console.error('Confirm payment error:', err);
      }
      setSucceeded(true);
      setIsProcessing(false);
      setTimeout(() => onSuccess(), 1500);
    } else {
      setErrorMessage('Payment was not completed. Please try again.');
      setIsProcessing(false);
    }
  };

  if (succeeded) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-4">
        <div className="w-16 h-16 rounded-full bg-green-500/15 border border-green-500/40 flex items-center justify-center">
          <CheckCircle size={32} className="text-green-400" />
        </div>
        <h3 className="text-lg font-bold text-foreground">Payment Successful!</h3>
        <p className="text-sm text-muted-foreground text-center">
          Your {paymentType} of ${amount.toLocaleString()} has been received.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <div className="rounded-xl bg-primary/5 border border-primary/20 p-4">
        <p className="text-xs font-mono text-primary uppercase tracking-wider mb-1">
          {paymentType === 'deposit' ? 'Deposit Payment' : 'Balance Payment'}
        </p>
        <p className="text-sm text-muted-foreground">{eventDescription}</p>
        <p className="text-2xl font-extrabold gold-gradient-text font-mono mt-1">
          ${amount.toLocaleString()}
        </p>
      </div>

      <PaymentElement
        options={{
          layout: 'tabs',
        }}
      />

      {errorMessage && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3">
          <p className="text-sm text-red-400">{errorMessage}</p>
        </div>
      )}

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Lock size={11} className="text-primary/60 shrink-0" />
        <span>Secured by Stripe. Your payment info is encrypted and never stored on our servers.</span>
      </div>

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onClose}
          disabled={isProcessing}
          className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-border text-muted-foreground hover:border-primary/40 hover:text-foreground transition-all duration-200 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isProcessing ? (
            <>
              <Loader2 size={15} className="animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard size={15} />
              Pay ${amount.toLocaleString()}
            </>
          )}
        </button>
      </div>
    </form>
  );
}

const stripePromise = getStripe();

export default function StripePaymentModal({
  isOpen,
  onClose,
  onSuccess,
  paymentType,
  amount,
  clientSecret,
  paymentScheduleId,
  eventDescription,
  clientEmail,
  clientName,
}: StripePaymentModalProps) {
  if (!isOpen || !clientSecret) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl neon-border-gold card-glass p-7 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center">
              <CreditCard size={15} className="text-primary" />
            </div>
            <h2 className="text-base font-bold text-foreground">
              {paymentType === 'deposit' ? 'Pay Deposit' : 'Pay Balance'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-muted/40 hover:bg-muted/70 flex items-center justify-center transition-colors"
          >
            <X size={15} className="text-muted-foreground" />
          </button>
        </div>

        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              theme: 'night',
              variables: {
                colorPrimary: '#d4af37',
                colorBackground: '#0f0f0f',
                colorText: '#f5f5f5',
                colorDanger: '#ef4444',
                fontFamily: 'system-ui, sans-serif',
                borderRadius: '8px',
              },
            },
          }}
        >
          <PaymentForm
            onSuccess={() => onSuccess(paymentType)}
            onClose={onClose}
            paymentScheduleId={paymentScheduleId}
            amount={amount}
            paymentType={paymentType}
            eventDescription={eventDescription}
            clientEmail={clientEmail}
            clientName={clientName}
          />
        </Elements>
      </div>
    </div>
  );
}
